import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer, CategoryIcon, MobileBottomNav } from '../components/Layout';
import { betsApi } from '../services/api';
import { useWebSocket } from '../context/WebSocketContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Search, Filter, Clock, Users, Plus, ArrowRight,
  TrendingUp, Sparkles, X
} from 'lucide-react';

const categories = [
  { id: 'all', name: 'All' },
  { id: 'sports', name: 'Sports' },
  { id: 'entertainment', name: 'Entertainment' },
  { id: 'weather', name: 'Weather' },
  { id: 'news', name: 'News' },
  { id: 'politics', name: 'Politics' },
  { id: 'wildcard', name: 'Wildcard' }
];

const BrowseBets = () => {
  const { addListener } = useWebSocket() || {};
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBets = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      // Fetch both open and active bets
      const [openRes, activeRes] = await Promise.all([
        betsApi.browse({ ...params, status: 'open' }),
        betsApi.browse({ ...params, status: 'active' })
      ]);
      const allBets = [...(openRes.data.bets || []), ...(activeRes.data.bets || [])];
      setBets(allBets);
    } catch (err) {
      toast.error('Failed to load bets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBets();
  }, [selectedCategory]);

  // Real-time updates via WebSocket
  useEffect(() => {
    if (!addListener) return;
    return addListener((event) => {
      if (['bet_created', 'bet_matched', 'bet_approved'].includes(event.type)) {
        fetchBets();
      }
    });
  }, [addListener, selectedCategory]);

  const [selectedBetForMatch, setSelectedBetForMatch] = useState(null);

  const filteredBets = bets.filter(bet => 
    bet.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bet.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleMatchBet = async (betId) => {
    try {
      await betsApi.match({ bet_id: betId, chosen_option: 'b' });
      toast.success('Bet matched successfully!');
      setSelectedBetForMatch(null);
      fetchBets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match bet');
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0 overflow-x-hidden">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }} data-testid="browse-title">
              Browse <span className="text-[#D4AF37]">Bets</span>
            </h1>
            <p className="text-gray-400 text-sm">Find and match bets or create your own</p>
          </div>
          <Link to="/create-bet">
            <Button className="btn-primary" data-testid="create-bet-btn">
              <Plus className="h-4 w-4 mr-2" /> Create Bet
            </Button>
          </Link>
        </div>

        {/* Search & Filter */}
        <div className="card-dark mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search bets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-dark pl-10 w-full"
                data-testid="search-input"
              />
            </div>
          </div>
          
          {/* Category Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`tab-3d text-sm whitespace-nowrap flex-shrink-0 ${selectedCategory === cat.id ? 'active' : ''}`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.id !== 'all' && <CategoryIcon category={cat.id} className="h-4 w-4 inline mr-1" />}
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredBets.length === 0 ? (
          <div className="card-dark text-center py-12">
            <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-500" />
            <h3 className="font-bold text-lg text-white mb-2">No bets found</h3>
            <p className="text-gray-400 mb-4">Be the first to create a bet in this category!</p>
            <Link to="/create-bet">
              <Button className="btn-primary">Create Bet</Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Unmatched Bets - Match my BET */}
            {filteredBets.filter(b => !b.matched && b.status === 'open').length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                  <TrendingUp className="h-5 w-5 inline mr-2 text-[#D4AF37]" />
                  Match my <span className="text-[#D4AF37]">BET</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredBets.filter(b => !b.matched && b.status === 'open').map((bet) => (
                    <div key={bet.id} className="card-dark card-dark-interactive border-[#D4AF37]/30 min-w-0" data-testid={`open-bet-${bet.id}`}>
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <span className="badge-gold text-xs flex-shrink-0">{bet.category}</span>
                        <div className="font-bold text-[#D4AF37] text-sm whitespace-nowrap">
                          {bet.stake_type === 'real' ? '$' : ''}{bet.stake_amount}{bet.stake_type === 'qbean' ? ' QBC' : ''}
                        </div>
                      </div>
                      <h3 className="font-bold text-base text-white mb-1 line-clamp-2 break-words">{bet.title}</h3>
                      <p className="text-sm text-gray-400 line-clamp-2 break-words mb-2">{bet.description}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-3 flex-wrap">
                        <Clock className="h-3 w-3 flex-shrink-0" />
                        <span>{new Date(bet.deadline).toLocaleDateString()}</span>
                        <span className="ml-auto text-[#D4AF37] truncate max-w-[120px]">{bet.creator_name}</span>
                      </div>
                      <Button 
                        onClick={() => setSelectedBetForMatch(bet)}
                        className="btn-green w-full text-sm"
                        data-testid={`match-bet-${bet.id}`}
                      >
                        Match my BET
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matched Bets - Live Bets */}
            {filteredBets.filter(b => b.matched || b.status === 'active').length > 0 && (
              <div>
                <h2 className="text-xl font-bold uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                  <Users className="h-5 w-5 inline mr-2 text-[#28A745]" />
                  Live <span className="text-[#28A745]">Matched Bets</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredBets.filter(b => b.matched || b.status === 'active').map((bet) => (
                    <Link key={bet.id} to={`/bet/${bet.id}`}>
                      <div className="card-dark card-dark-interactive border-[#28A745]/30 min-w-0" data-testid={`matched-bet-${bet.id}`}>
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <span className="badge-green text-xs flex-shrink-0">{bet.category}</span>
                          <div className="font-bold text-[#28A745] text-sm whitespace-nowrap">
                            {bet.stake_type === 'real' ? '$' : ''}{bet.stake_amount * 2}{bet.stake_type === 'qbean' ? ' QBC' : ''} pot
                          </div>
                        </div>
                        <h3 className="font-bold text-base text-white mb-2 line-clamp-2 break-words">{bet.title}</h3>
                        
                        {/* Player vs Player */}
                        <div className="bg-[#2D2D2D] rounded-lg p-2 flex items-center justify-between min-w-0 gap-1">
                          <div className="flex items-center gap-1 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#1A1A1A] font-bold text-xs flex-shrink-0">
                              {bet.creator_name?.charAt(0)}
                            </div>
                            <span className="text-white font-semibold text-xs truncate">{bet.creator_name}</span>
                          </div>
                          <span className="text-[#D4AF37] font-black text-xs flex-shrink-0 px-1">VS</span>
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-white font-semibold text-xs truncate">{bet.matcher_name || '???'}</span>
                            <div className="w-7 h-7 rounded-full bg-[#28A745] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                              {bet.matcher_name?.charAt(0) || '?'}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                          <Clock className="h-3 w-3 flex-shrink-0" />
                          <span>{new Date(bet.deadline).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Bet Details + Rules Acceptance Modal */}
      {selectedBetForMatch && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="card-dark w-full max-w-lg my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="badge-gold text-xs">{selectedBetForMatch.category}</span>
                {selectedBetForMatch.bet_number && <span className="text-xs text-gray-500 ml-2">{selectedBetForMatch.bet_number}</span>}
              </div>
              <button onClick={() => setSelectedBetForMatch(null)} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <h2 className="text-xl font-black text-white mb-3" style={{ fontFamily: 'Outfit' }}>{selectedBetForMatch.title}</h2>
            <p className="text-gray-400 mb-4">{selectedBetForMatch.description}</p>
            
            {selectedBetForMatch.rules && (
              <div className="bg-[#2D2D2D] rounded-lg p-4 mb-4 border border-[#D4AF37]/30">
                <h3 className="text-[#D4AF37] font-bold text-sm mb-2">BET RULES</h3>
                <p className="text-white text-sm">{selectedBetForMatch.rules}</p>
              </div>
            )}
            
            <div className="bg-[#2D2D2D] rounded-lg p-3 mb-4 flex justify-between text-sm">
              <div><span className="text-gray-400">Stake:</span> <span className="text-[#D4AF37] font-bold">{selectedBetForMatch.stake_amount} {selectedBetForMatch.stake_type === 'qbean' ? 'QBC' : 'USD'}</span></div>
              <div><span className="text-gray-400">Deadline:</span> <span className="text-white font-bold">{new Date(selectedBetForMatch.deadline).toLocaleString()}</span></div>
            </div>
            <div className="text-sm text-gray-400 mb-4">
              <span className="text-gray-500">Initiator:</span> <span className="text-[#D4AF37]">{selectedBetForMatch.creator_name}</span>
            </div>
            
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-[#D4AF37]">By clicking "Accept & Match", you accept the rules and conditions of this bet and agree to stake <strong>{selectedBetForMatch.stake_amount} {selectedBetForMatch.stake_type === 'qbean' ? 'QBC' : 'USD'}</strong> from your wallet.</p>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={() => setSelectedBetForMatch(null)} className="btn-secondary flex-1">Cancel</Button>
              <Button onClick={() => handleMatchBet(selectedBetForMatch.id)} className="btn-green flex-1" data-testid="accept-match-btn">
                Accept & Match
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default BrowseBets;
